'use strict';

define('vb/private/stateManagement/layoutExtension',[
  'vb/helpers/mixin',
  'vb/private/stateManagement/containerExtension',
  'vb/private/stateManagement/context/layoutExtensionContext',
  'vb/private/stateManagement/layoutMixin',
  'vb/private/stateManagement/fragmentHolderExtensionMixin',
  'vb/private/stateManagement/eventBehaviorMixin',
  'vb/private/utils',
  'vb/private/constants',
  'vb/private/stateManagement/baseModuleViewModel',
  'vb/private/translations/bundleUtils',
], (Mixin, ContainerExtension, LayoutExtensionContext, LayoutMixin, FragmentHolderExtensionMixin,
  EventBehaviorMixin, Utils, Constants, BaseModuleViewModel, BundleUtils) => {
  /** @typedef {import('vb/private/stateManagement/layout').default} Layout */

  /**
   * LayoutExtension class
   */
  class LayoutExtension extends Mixin(ContainerExtension)
    .with(FragmentHolderExtensionMixin, LayoutMixin, EventBehaviorMixin) {
    constructor(extension, path, base, className = 'LayoutExtension') {
      // layout doesn't have a parent since it's not part of the container hierarchy
      super(extension, null, path, base, className);

      this.descriptor = undefined;
      this.template = undefined;

      this.resourceDescriptor = base.resourceDescriptor;
      this.serializer = this.resourceDescriptor ? this.resourceDescriptor.serializer : null;
      this.deserializer = this.resourceDescriptor ? this.resourceDescriptor.deserializer : null;
    }

    static get extensionClass() {
      return LayoutExtension;
    }

    /**
     * The default event prefix is the lowercase class name (see container.js) but for
     * layout extension we want to use the same event prefix as layout
     *
     * @type {String}
     */
    // eslint-disable-next-line class-methods-use-this
    get eventPrefix() {
      return 'layout';
    }

    /**
     * Map the namespace for a layout extension to the extension id.
     *
     * @returns {String}
     */
    get layoutNamespace() {
      return this.extensionId;
    }

    descriptorLoader(resourceLocator) {
      const resourcePath = `${resourceLocator}-x.json`;

      return Promise.resolve().then(() => {
        if (this.deserializer) {
          return this.deserializer.getTextResource(resourcePath);
        }
        return super.descriptorLoader(resourcePath)
          .then((jsonContent) => {
            if (this.serializer) {
              this.serializer.addTextResource(resourcePath, jsonContent);
            }
            return jsonContent;
          });
      }).then((jsonContent) => {
        this.descriptor = jsonContent; // unparsed content
        return Utils.parseJsonResource(jsonContent, resourcePath);
      });
    }

    functionsLoader(resourceLocator) {
      return super.functionsLoader(`${resourceLocator}-x`);
    }

    templateLoader(resourceLocator) {
      return Promise.resolve().then(() => {
        const resourcePath = `${resourceLocator}-x.html`;
        if (this.deserializer) {
          return this.deserializer.getTextResource(resourcePath);
        }
        return super.templateLoader(resourcePath).then((template) => {
          if (this.serializer) {
            this.serializer.addTextResource(resourcePath, template);
          }
          return template;
        });
      }).then((template) => {
        this.template = template;
        return template;
      });
    }

    /**
     * Returns a value that is appropriate to the option that is passed in.
     * @param option
     * @returns {*}
     */
    // eslint-disable-next-line class-methods-use-this
    getCapability(option) {
      if (option === Constants.FragmentCapability.ALLOWS_SLOTS) {
        // fragment slots are disallowed in layouts
        return false;
      }
      return null;
    }

    /**
     * @type {String}
     */
    static get resourceSuffix() {
      return '-x.json';
    }

    /**
     * The name of the runtime environment function to be used to load the descriptor file.
     *
     * @type {String} the descriptor loader function name
     */
    static get descriptorLoaderName() {
      return 'getExtensionTextResource';
    }

    /**
     * The name of the runtime environment function to be used to load the module functions.
     *
     * @type {String} the module loader function name
     */
    static get functionsLoaderName() {
      return 'getExtensionModuleResource';
    }

    /**
     * The name of the runtime environment function to be used to load the html.
     *
     * @type {String} the template loader function name
     */
    static get templateLoaderName() {
      return 'getExtensionTextResource';
    }

    /**
     * Returns the model object for the layout container. The model contains all the accessible $ properties
     * of the layout container. All properties are initialized and ready to be evaluated in expressions.
     * Extensions have also been applied to the layout.
     * The model is bound to the layout view by the JET dynamic component.
     * TODO: it's unclear if this method override returns a Map of contexts, similar to PageExtension, rather than
     * the new XYZModuleViewModel instance as the layout does. See comments on PageExtension for the reason
     *
     * {
     *   $variables: {}
     *   $constants: {}
     *   $chains: {}
     *   $functions: {}
     *   $listeners: {}
     *   $translations: {}
     *
     *   @todo: $extension.variables (tracked VBS-1793)
     * }
     *
     * @return {Promise<Object>} a promise that resolve with the model object.
     */
    getViewModel() {
      if (!this.viewModelPromise) {
        this.viewModelPromise = Promise.all([
          this.load(), this.loadFunctionModule(), this.loadTemplate(),
        ])
          // load() eventually triggers loadTranslationBundles().  None of the setup needs to block on the load of the
          // bundles, but we need to make sure the bundles have been loaded before we try to construct $translations.
          .then(() => BundleUtils.whenBundlesReady())
          // TODO: it's unclear whether a new module viewModel instance needs to be created here or the one from base
          //  can be returned. We are providing separate layout data models to the layout components - one for the
          //  main (base) and for each extension. The dataModel includes the VB view model instance that's passed in to
          //  sub-CCAs like fragment by CCA framework. So vbBridge is needed
          .then(() => {
            this.viewModel = new BaseModuleViewModel(this);
            return this.viewModel;
          });
      }
      return this.viewModelPromise;
    }

    /**
     * Returns the ContainerExtensionContext constructor used to create the '$' expression context.
     * @type {typeof LayoutExtensionContext}
     */
    static get ContextType() {
      return LayoutExtensionContext;
    }

    /**
     * Returns a scope resolver map where keys are scope name ("page", "flow" or "application")
     * and value the matching objects. This is used to build the scopeResolver object.
     *
     * @return {Object} an object which properties are scope
     */
    getScopeResolverMap() {
      const map = {
        [Constants.LAYOUT_PREFIX]: this,
      };

      if (this.extensionId !== Constants.ExtensionNamespaces.BASE) {
        // for now expose global scope to layouts in extensions so fragments referenced from layouts can have access
        // to global scope.
        Object.assign(map, {
          [Constants.GLOBAL_PREFIX]: this.application,
        });
      } else {
        Object.assign(map, {
          [Constants.APPLICATION_PREFIX]: this.application,
        });
      }

      return map;
    }

    /**
     * Used by event processing. For LayoutExtension containers, we start (and end) with the Layout itself.
     * Overrides Container#getLeafContainer.
     * @see FireCustomEventAction
     * @see Container#getLeafContainer
     * @returns {Layout}
     */
    getLeafContainer() {
      return this.base;
    }
  }

  return LayoutExtension;
});

